import React from 'react';
import MetricSummaryTrendCardLoading from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCardLoading';
import {
  MetricSummaryTrendCardProps,
  MetricSummaryTrendCardLarge
} from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCard';
import { MetricsSummaryTrendCardSmall } from 'src/components/BeaconV2Components/MetricSummaryTrendCard/MetricSummaryTrendCard';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import { getAppName } from 'src/utils/app';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import _get from 'lodash/get';
import { ValueOf } from 'sl-api-connector';

export const getCardTitle = (
  indexName: string,
  fieldName: string,
  titleOverrides?: Record<string, Record<string, string>>
) => {
  return (
    _get(titleOverrides, [indexName, fieldName]) ||
    INDEX_FIELDS.getField(getAppName(), indexName, fieldName).displayName
  );
};

export const getCardMetricType = (indexName: string, fieldName: string) => {
  return INDEX_FIELDS.getField(getAppName(), indexName, fieldName).metricType;
};

/**
 * Key metric card that accepts metrics data as props instead of fetching it
 * Maintains the exact same styling but allows for more control over data source
 */
interface StaticKeyMetricCardProps {
  indexName?: string;
  fieldName: string;
  metricTypeToDisplay?: ValueOf<typeof METRICTYPE>;
  variant: 'large' | 'small' | 'medium';
  title?: string;
  onClick?: () => void;
  buildMetricSummaryTrendCardProps?: (defaultProps: MetricSummaryTrendCardProps) => MetricSummaryTrendCardProps;
  isLoading?: boolean;
  primaryData: any[];
  secondaryData: any[];
  formattedPrimaryTotal: string;
  formattedPercentChange: string;
  percentChange: number;
  formattedLastPrimaryValue?: string;
  formattedPercentChangeLastValues?: string;
  percentChangeLastValues?: number;
  useLatestWeek?: boolean;
}

const StaticKeyMetricCard = ({
  indexName,
  fieldName,
  metricTypeToDisplay,
  variant,
  title,
  onClick,
  buildMetricSummaryTrendCardProps,
  isLoading = false,
  primaryData,
  secondaryData,
  formattedPrimaryTotal,
  formattedPercentChange,
  percentChange,
  formattedLastPrimaryValue,
  formattedPercentChangeLastValues,
  percentChangeLastValues,
  useLatestWeek = false
}: StaticKeyMetricCardProps) => {
  const mainMetric = useLatestWeek ? formattedLastPrimaryValue : formattedPrimaryTotal;
  const rawChange = useLatestWeek ? percentChangeLastValues : percentChange;
  const change = useLatestWeek ? formattedPercentChangeLastValues : formattedPercentChange;

  const getMetricSummmaryTrendCardProps = (): MetricSummaryTrendCardProps => {
    const defaultProps: MetricSummaryTrendCardProps = {
      title: title || getCardTitle(indexName, fieldName),
      splinePreviewProps: {
        metricType: metricTypeToDisplay || getCardMetricType(indexName, fieldName),
        primaryData,
        secondaryData
      },
      value: mainMetric,
      change,
      direction: getDirection(rawChange),
      selected: false,
      onClick
    };

    return buildMetricSummaryTrendCardProps ? buildMetricSummaryTrendCardProps(defaultProps) : defaultProps;
  };

  return isLoading ? (
    <MetricSummaryTrendCardLoading variant={variant} />
  ) : variant === 'small' ? (
    <MetricsSummaryTrendCardSmall {...getMetricSummmaryTrendCardProps()} />
  ) : (
    <MetricSummaryTrendCardLarge variant={variant} {...getMetricSummmaryTrendCardProps()} />
  );
};

export default StaticKeyMetricCard;
